import { useRoutes, useUrlGenerator } from '@folklore/routes';
import { useIntl } from 'react-intl';
import { Route, Switch } from 'wouter';

import { typesSections } from '../routes';
import AuthorMetadata from './metadata/AuthorMetadata';
import CollectionMetadata from './metadata/CollectionMetadata';
import DocumentMetadata from './metadata/DocumentMetadata';
import HomeMetadata from './metadata/HomeMetadata';
import MapMetadata from './metadata/MapMetadata';
import Metadata from './metadata/Metadata';
import NotfoundMetadata from './metadata/NotfoundMetadata';
import PageMetadata from './metadata/PageMetadata';
import SearchMetadata from './metadata/SearchMetadata';
import SubscribeMetadata from './metadata/SubscribeMetadata';
import TaxonomyMetadata from './metadata/TaxonomyMetadata';

function MetadataRoutes() {
    const intl = useIntl();
    const routes = useRoutes();
    const url = useUrlGenerator();
    return (
        <Switch>
            <Route path={routes.collection}>
                <CollectionMetadata />
            </Route>
            <Route path={routes.author}>
                <AuthorMetadata />
            </Route>
            <Route path={routes.category}>
                <TaxonomyMetadata type="category" />
            </Route>
            <Route path={routes.topic}>
                <TaxonomyMetadata type="topic" />
            </Route>
            <Route path={routes.format}>
                <TaxonomyMetadata type="format" />
            </Route>

            <Route path={routes.sponso}>
                {(params) => {
                    const { type: typeSlug = null } = params;
                    const { label = null } =
                        typesSections.find(
                            ({ id, path }) => id === typeSlug || path === typeSlug,
                        ) || {};
                    return (
                        <Metadata
                            title={
                                label !== null
                                    ? intl.formatMessage(
                                          {
                                              defaultMessage: 'Sponsos - {label}',
                                              description: 'Page title',
                                          },
                                          {
                                              label,
                                          },
                                      )
                                    : intl.formatMessage({
                                          defaultMessage: 'Sponsos',
                                          description: 'Page title',
                                      })
                            }
                            url={url('sponso', params)}
                            pageType="index"
                        />
                    );
                }}
            </Route>

            {typesSections.map(({ id, title, all = false }) => (
                <Route key={`section-${id}`} path={routes[id]}>
                    {(params) => (
                        <Metadata
                            title={title}
                            url={url(id, params)}
                            pageType={all ? id : 'index'}
                        />
                    )}
                </Route>
            ))}

            {typesSections
                .filter(({ all = false }) => all)
                .map(({ id, indexTitle, title }) => (
                    <Route key={`section-${id}-all`} path={routes[`${id}.all`]}>
                        {(params) => (
                            <Metadata
                                title={indexTitle || title}
                                url={url(`${id}.all`, params)}
                                pageType="index"
                            />
                        )}
                    </Route>
                ))}

            <Route path={routes.search}>
                <SearchMetadata />
            </Route>

            <Route path={routes.map}>
                <MapMetadata />
            </Route>
            <Route path={routes['map.region']}>
                <MapMetadata />
            </Route>
            <Route path={routes['map.locationType']}>
                <MapMetadata />
            </Route>
            <Route path={routes['map.location']}>
                <MapMetadata />
            </Route>
            <Route path={routes['map.document']}>
                <MapMetadata />
            </Route>

            <Route path={routes.login}>
                <Metadata
                    title={intl.formatMessage({
                        defaultMessage: 'Connexion',
                        description: 'Page title',
                    })}
                    url={url('login')}
                    pageType="login"
                />
            </Route>

            <Route path={routes.register}>
                <Metadata
                    title={intl.formatMessage({
                        defaultMessage: 'Inscription',
                        description: 'Page title',
                    })}
                    url={url('register')}
                    pageType="register"
                />
            </Route>

            <Route path={routes.account}>
                <Metadata
                    title={intl.formatMessage({
                        defaultMessage: 'Compte',
                        description: 'Page title',
                    })}
                    url={url('account')}
                    pageType="account"
                    noIndex
                />
            </Route>

            <Route path={routes['account.profile']}>
                <Metadata
                    title={intl.formatMessage({
                        defaultMessage: 'Profil - Compte',
                        description: 'Page title',
                    })}
                    pageType="account"
                    noIndex
                />
            </Route>
            <Route path={routes['account.subscriptions']}>
                <Metadata
                    title={intl.formatMessage({
                        defaultMessage: 'Abonnements - Compte',
                        description: 'Page title',
                    })}
                    pageType="account"
                    noIndex
                />
            </Route>
            <Route path={routes['account.timeline']}>
                <Metadata
                    title={intl.formatMessage({
                        defaultMessage: 'Historique - Compte',
                        description: 'Page title',
                    })}
                    pageType="account"
                    noIndex
                />
            </Route>
            <Route path={routes['account.promotions']}>
                <Metadata
                    title={intl.formatMessage({
                        defaultMessage: 'Avantages - Compte',
                        description: 'Page title',
                    })}
                    pageType="account"
                    noIndex
                />
            </Route>
            <Route path={routes['account.stats']}>
                <Metadata
                    title={intl.formatMessage({
                        defaultMessage: 'Statistiques - Compte',
                        description: 'Page title',
                    })}
                    pageType="account"
                    noIndex
                />
            </Route>

            <Route path={routes.subscription}>
                <SubscribeMetadata />
            </Route>

            <Route path={routes['subscription.friend']}>
                <SubscribeMetadata friend />
            </Route>

            <Route path={routes.subscribe}>
                <SubscribeMetadata noIndex />
            </Route>

            <Route path={routes.document}>
                <DocumentMetadata />
            </Route>
            <Route path={routes.preview}>
                <DocumentMetadata isPreview noIndex />
            </Route>

            <Route path={routes.section}>
                <HomeMetadata />
            </Route>
            <Route path={routes.home}>
                <HomeMetadata />
            </Route>

            <Route path={routes.page}>
                <PageMetadata />
            </Route>
            <Route>
                <NotfoundMetadata />
            </Route>
        </Switch>
    );
}

export default MetadataRoutes;
